<template>
  <div>
    <Loading v-if="loading" type="bubbles" :center="false" :width="40" :height="40" />
    <div v-else class="rent-planner-links">
      <div class="rent-planner-links-bar">
        <template>
          <div class="rent-planner-links-label">
            <i class="fa-solid fa-chart-kanban"></i>
            <span>Planner</span>
          </div>

          <Button
              _key="btnOpenCreatePlannerCard"
              type="primary"
              classIcon="fa-solid fa-plus"
              size="small"
              :clicked="openCreate"
          />

          <div class="rent-planner-links-content">
          <span v-if="cards.length === 0" class="rent-planner-links-empty">
            Nenhum cartão vinculado
          </span>

            <div v-else class="rent-planner-links-pills">
              <div
                  v-for="card in cards"
                  :key="card.cardId"
                  class="rent-planner-links-pill"
                  @click="openCardInfo(card)"
                  :title="`${card.funnelName} · ${card.stepName} — Ver informações`"
              >
                <span class="rent-planner-links-pill-dot" :style="{ background: card.stepColor }"></span>
                <span class="rent-planner-links-pill-step">{{ card.stepName }}</span>
                <span class="rent-planner-links-pill-sep">·</span>
                <span class="rent-planner-links-pill-funnel">{{ card.funnelName }}</span>
                <i class="fa-solid fa-circle-info rent-planner-links-pill-icon"></i>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <Modal
        title="Criar Cartão"
        :width="800"
        v-if="showModal('rentPlannerLinksCreate')"
    >
      <RentPlannerCreate :rentId="rentId" :customer="customer"/>
    </Modal>

    <Modal
        title="Cartão do Planner"
        :width="450"
        v-if="showModal('rentPlannerLinksCardInfo')"
    >
      <div class="rent-planner-links-info">
        <div class="rent-planner-links-info-row">
          <span class="rent-planner-links-info-label">Quadro</span>
          <span class="rent-planner-links-info-value">{{ selectedCard.funnelName }}</span>
        </div>
        <div class="rent-planner-links-info-row">
          <span class="rent-planner-links-info-label">Etapa</span>
          <span class="rent-planner-links-info-value">
            <span class="rent-planner-links-pill-dot" :style="{ background: selectedCard.stepColor }"></span>
            {{ selectedCard.stepName }}
          </span>
        </div>

        <div class="rent-planner-links-info-actions">
          <Button
              _key="btnEditPlannerCard"
              type="edit"
              title="Editar cartão"
              classIcon="fas fa-pen"
              size="medium"
              :loading="isLoading('btnEditPlannerCard')"
              :clicked="openCardEdit"
          />
          <Button
              _key="btnGoToFullBoard"
              type="primary"
              title="Ir para o Quadro"
              classIcon="fa-solid fa-clone"
              size="medium"
              :clicked="goToKanban"
          />
        </div>
      </div>
    </Modal>

    <Modal
        title="Editar cartão"
        :width="1200"
        v-if="showModal('rentPlannerLinksCardEdit')"
    >
      <div v-if="editingCard" class="rent-planner-links-edit-step">
        <StepSelector :card="editingCard" />
      </div>
      <ScrollBar :maxHeight="600">
        <CardEditPanel v-if="editingCard" :key="editingCard.id" :card="editingCard" />
      </ScrollBar>
    </Modal>
  </div>

</template>

<script>
import Button from "@/ui/forms/Button";
import Loading from "@/ui/shared/Loading.vue";
import Modal from "@/ui/forms/Modal";
import ScrollBar from "@/ui/layout/ScrollBar.vue";

import RentPlannerCreate from "./RentPlannerCreate.vue";
import CardEditPanel from "../card/CardEditPanel.vue";
import StepSelector from "../step/StepSelector.vue";
import CardService from "../../services/card.service";

import {mapGetters, mapMutations, mapState} from "vuex";

export default {
  name: "RentPlannerLinks",
  components: {Button, Loading, Modal, ScrollBar, RentPlannerCreate, CardEditPanel, StepSelector},
  props: {
    rentId: {
      type: String,
      required: true,
    },
    customer: {
      type: Object,
      default: () => ({id: "", content: ""}),
    },
  },
  data() {
    return {
      loading: false,
      cards: [],
      selectedCard: {},
      editingCard: null,
    };
  },
  mounted() {
    if (this.rentId) this.loadCards();
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapState("generic", ["modal"]),
    ...mapGetters("generic", ["showModal", "isLoading", "event"]),
  },
  methods: {
    ...mapMutations("generic", ["openModal", "hideModal", "addLoading", "removeLoading"]),
    async loadCards() {
      if (!this.rentId) return;
      this.loading = true;
      try {
        const response = await CardService.getByRentId(this.rentId, this.userLogged.user.id);
        const cards = response.data.content.data || [];
        this.cards = cards.filter((card) => this.canViewCard(card));
      } finally {
        this.loading = false;
      }
    },
    canViewCard(card) {
      if (card.shared === undefined || card.shared === null || card.shared) return true;
      const allowedUsers = card.allowedUsers || [];
      return allowedUsers.includes(this.userLogged.user.id) || allowedUsers.includes(this.userLogged.user.userName);
    },
    openCreate() {
      this.openModal("rentPlannerLinksCreate");
      this.removeLoading(["btnOpenCreatePlannerCard"]);
    },
    openCardInfo(card) {
      this.selectedCard = card;
      this.openModal("rentPlannerLinksCardInfo");
    },
    async openCardEdit() {
      this.addLoading("btnEditPlannerCard");
      try {
        const response = await CardService.getAll(this.selectedCard.funnelId, []);
        const cards = response.data.content.data || [];
        this.editingCard = cards.find((card) => card.id === this.selectedCard.cardId) || null;
        if (this.editingCard) {
          this.hideModal();
          this.openModal("rentPlannerLinksCardEdit");
        }
      } finally {
        this.removeLoading(["btnEditPlannerCard"]);
      }
    },
    goToKanban() {
      this.hideModal();
      this.$router.push({
        path: `/planner/kanban/${this.selectedCard.funnelId}/${encodeURIComponent(this.selectedCard.funnelName)}`,
      });
    },
  },
  watch: {
    rentId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) this.loadCards();
    },
    event: {
      handler(event) {
        if (event.name === "plannerCardCreated") this.loadCards();
      },
      deep: true,
    },
    modal: {
      handler(newModal, oldModal) {
        if (oldModal.name === "rentPlannerLinksCardEdit" && newModal.name !== "rentPlannerLinksCardEdit") {
          this.editingCard = null;
          this.loadCards();
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.rent-planner-links {
  margin-bottom: 12px;
}

.rent-planner-links-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  flex-wrap: wrap;
}

.rent-planner-links-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
}

.rent-planner-links-label i {
  font-size: 12px;
  color: var(--color-brand);
}

.rent-planner-links-content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.rent-planner-links-empty {
  font-size: 13px;
  color: var(--text-muted);
  font-style: italic;
}

.rent-planner-links-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.rent-planner-links-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--bg-base);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.rent-planner-links-pill:hover {
  background: var(--bg-hover);
  border-color: var(--color-brand-light);
}

.rent-planner-links-pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
  display: inline-block;
}

.rent-planner-links-pill-step {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}

.rent-planner-links-pill-sep {
  font-size: 11px;
  color: var(--text-muted);
}

.rent-planner-links-pill-funnel {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rent-planner-links-pill-icon {
  font-size: 9px;
  color: var(--text-muted);
  flex-shrink: 0;
}

.rent-planner-links-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
}

.rent-planner-links-info-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rent-planner-links-info-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
}

.rent-planner-links-info-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--text-primary);
}

.rent-planner-links-info-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid var(--border-subtle);
  margin-top: 4px;
}

.rent-planner-links-edit-step {
  margin-bottom: 12px;
}
</style>
